<script setup lang="ts">
/**
 * The AI 助手 over the console's pages: the button at the bottom right —
 * round, or a capsule with the name down it (the admin's choice) — when
 * the admin offers it there, and the panel — opened from it
 * or from the navbar's cell (AssistantNavButton). Asks whether the
 * assistant is on when the console loads and each time the panel opens,
 * for today's count and the admin's settings.
 */
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/store/auth'
import { assistant, assistantName, loadAssistant, offered, toggleAssistant } from '@/utils/assistant'
import { isPhone } from '@/utils/responsive'
import AssistantMark from './AssistantMark.vue'
import AssistantPanel from './AssistantPanel.vue'

const { t, locale } = useI18n()
const auth = useAuthStore()
const info = computed(() => assistant.info)
const name = computed(() => assistantName(info.value, locale.value, t('assistant.defaultName')))
const floating = computed(() => offered('float'))
const capsule = computed(() => info.value?.launcher === 'capsule')

function load() {
  if (auth.isLoggedIn) void loadAssistant()
  else assistant.info = null
}
onMounted(load)
watch(() => auth.isLoggedIn, load)
watch(
  () => assistant.open,
  (v) => {
    if (v) load()
  },
)

// The floating 页面配置 button moves up off this one (App.vue): further
// for the taller capsule.
watch(
  [floating, capsule],
  ([on, tall]) => {
    document.body.classList.toggle('has-assistant', on && !tall)
    document.body.classList.toggle('has-assistant-tall', on && tall)
  },
  { immediate: true },
)
onBeforeUnmount(() => document.body.classList.remove('has-assistant', 'has-assistant-tall'))

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && assistant.open) assistant.open = false
}
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))

function asked() {
  if (assistant.info) assistant.info = { ...assistant.info, used_today: assistant.info.used_today + 1 }
}
</script>

<template>
  <template v-if="info?.enabled">
    <button
      v-if="floating && !(assistant.open && isPhone)"
      type="button"
      class="launcher"
      :class="{ open: assistant.open && assistant.from === 'float', capsule }"
      :title="assistant.open ? undefined : name"
      :aria-label="name"
      :aria-expanded="assistant.open"
      @click="toggleAssistant('float')"
    >
      <AssistantMark :icon="info.icon" :animated="info.animated" :size="capsule ? 28 : 30" />
      <span v-if="capsule" class="capsule-name">{{ name }}</span>
    </button>
    <AssistantPanel :open="assistant.open" :info="info" :from="assistant.from" @close="assistant.open = false" @asked="asked" />
  </template>
</template>

<style scoped>
/* Round, as Arco's floating buttons are; a layer over the page, so its
   light shadow stays. */
.launcher {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1001;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  padding: 0;
  border: 1px solid var(--color-border-1);
  border-radius: 50%;
  background: var(--color-bg-2);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  transition:
    transform 0.15s,
    box-shadow 0.15s;
}
.launcher:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.16);
}
.launcher.open {
  border-color: rgb(var(--primary-6));
}
/* The capsule: the mark over the name, written down it, as 火山助手's is. */
.launcher.capsule {
  flex-direction: column;
  justify-content: flex-start;
  gap: 8px;
  width: 46px;
  height: auto;
  padding: 10px 0 14px;
  border-radius: 23px;
}
.capsule-name {
  max-height: 96px;
  overflow: hidden;
  writing-mode: vertical-rl;
  letter-spacing: 3px;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-text-1);
}
.launcher:focus-visible {
  outline: 2px solid rgb(var(--primary-3));
  outline-offset: 2px;
}
@media (max-width: 767px) {
  .launcher {
    right: 16px;
    bottom: 16px;
    width: 48px;
    height: 48px;
  }
}
</style>
